// src/components/SEO.jsx
// Custom SEO component for React 19 (no external dependencies)
// Manages page-level meta tags and structured data

import { useEffect } from 'react';

const SITE_NAME = 'DreamableAI';
const SITE_URL = 'https://dreamableai.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

/**
 * SEO Component for managing page-level meta tags
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended with site name)
 * @param {string} props.description - Page meta description (max 160 chars recommended)
 * @param {string} props.path - Page path (e.g., '/subscribe')
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.type - Open Graph type (website, article, product)
 * @param {Object|Array} props.structuredData - Additional JSON-LD structured data
 * @param {boolean} props.noindex - Whether to add noindex tag
 */
export default function SEO({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  structuredData = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Create Beautiful AI Greeting Cards`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullDescription = description || 'Create beautiful, personalized greeting cards with AI. Design and send custom cards for birthdays, holidays, and special occasions with DreamableAI.';

  useEffect(() => {
    // Set document title
    document.title = fullTitle;

    // Helper to set or create meta tag
    const setMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    // Helper to set or create link tag
    const setLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic meta tags
    setMetaTag('meta[name="description"]', 'content', fullDescription);
    
    // Canonical URL
    setLinkTag('canonical', canonicalUrl);

    // Robots
    if (noindex) {
      setMetaTag('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // Open Graph / Facebook
    setMetaTag('meta[property="og:type"]', 'content', type);
    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl);
    setMetaTag('meta[property="og:title"]', 'content', fullTitle);
    setMetaTag('meta[property="og:description"]', 'content', fullDescription);
    setMetaTag('meta[property="og:image"]', 'content', image);
    setMetaTag('meta[property="og:site_name"]', 'content', SITE_NAME);

    // Twitter
    setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:url"]', 'content', canonicalUrl);
    setMetaTag('meta[name="twitter:title"]', 'content', fullTitle);
    setMetaTag('meta[name="twitter:description"]', 'content', fullDescription);
    setMetaTag('meta[name="twitter:image"]', 'content', image);

    // Structured Data (JSON-LD)
    // Remove any existing dynamic structured data
    const existingScripts = document.querySelectorAll('script[data-seo-structured]');
    existingScripts.forEach(script => script.remove());

    if (structuredData) {
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      dataArray.forEach((data, index) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-structured', `dynamic-${index}`);
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      // Remove dynamic structured data on unmount
      const scripts = document.querySelectorAll('script[data-seo-structured]');
      scripts.forEach(script => script.remove());
    };
  }, [fullTitle, fullDescription, canonicalUrl, image, type, noindex, structuredData]);

  // This component doesn't render anything
  return null;
}

/**
 * Helper to create BreadcrumbList structured data
 */
export function createBreadcrumbData(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

/**
 * Helper to create Product structured data for occasion pages
 */
export function createOccasionProductData(occasion) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${occasion.name} Greeting Cards`,
    description: occasion.description,
    brand: {
      '@type': 'Brand',
      name: 'DreamableAI',
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '0',
      highPrice: '9.99',
      offerCount: '2',
      availability: 'https://schema.org/InStock',
    },
    category: 'Greeting Cards',
  };
}


